import React, { useEffect, useMemo, useState } from "react";
import type { TagTree, TagType } from "@/app/types/tags";
import { Button } from "@/components/ui/Button";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/MobileToolTip";
import { toast } from "@/components/ui/UseToast";
import { allowedChildrenByTagType, typeIconMap } from "@/constants/tags";
import { useDeleteTagMutation, useGetTagsQuery } from "@/services/TagsApi";
import { createTagTreeFromTagNodes } from "@/utils/Helper";
import { Building2, ChevronDown, ChevronRight, Folder, Pencil, Plus, Trash2, Wrench } from "lucide-react";
import TagsForm from "@/components/admin/tags/components/modal/form";
import { ConfirmationDialog } from "@/components/ui/common/ConfirmationDailog";
import TagImportModal from "@/components/admin/tags/components/modal/import-modal";

import { useSelector } from "react-redux";
import { RootState } from "@/app/store";

const { VITE_ORG_ID } = import.meta.env;
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "tag_id",
};


const filterTreeByName = (nodes: TagTree[], query: string): TagTree[] => {
  if (!query.trim()) return nodes;
  const searchValue = query.trim().toLowerCase();

  return nodes
    .map((node) => {
      const filteredChildren = filterTreeByName(node.children, searchValue);
      const selfMatch = node.name.toLowerCase().includes(searchValue);
      if (selfMatch || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    })
    .filter((node): node is TagTree => Boolean(node));
};

const TagsTable = ({ eventId }: { eventId?: number }) => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const ORG_ID = Number(userInfo?.org_id || VITE_ORG_ID || 1);
  const { setSearchCriteria } =
    useTableData(initialValue);
  const [search, setSearch] = useState<string>("");
  const { data: tagsResponse, isLoading, refetch } = useGetTagsQuery({ 
    org_id: ORG_ID,
    event_ids: eventId ? [eventId] : undefined
  });
  const {
    data: tags = [],
    total_count = 0, 
    filtered_count = 0, 
  } = tagsResponse ?? {};
  const [deleteTag, { isLoading: isDeleting }] = useDeleteTagMutation();
  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [tagId, setTagId] = useState<number | undefined>(undefined);
  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [prefillParentTagId, setPrefillParentTagId] = useState<number | undefined>(undefined);
  const [prefillType, setPrefillType] = useState<TagType | undefined>(undefined);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [deleteTarget, setDeleteTarget] = useState<TagTree | null>(null);

  const filteredTagsData = useMemo(() => {
    if (!eventId) {
      // Global view: only show tags with no event_id
      return tags.filter((tag) => tag.event_id === null);
    }
    // Event view: show event-specific tags AND global tags (to maintain tree structure)
    return tags.filter(
      (tag) =>
        (tag.event_id !== null && Number(tag.event_id) === Number(eventId)) ||
        tag.event_id === null,
    );
  }, [tags, eventId]);

  const tagTree = useMemo(() => createTagTreeFromTagNodes(filteredTagsData), [filteredTagsData]);
  const filteredTree = useMemo(() => filterTreeByName(tagTree, search), [tagTree, search]);

  useEffect(() => {
    const idsWithChildren: number[] = [];
    const collect = (nodes: TagTree[]) => {
      for (const node of nodes) {
        if (node.children.length > 0) {
          idsWithChildren.push(node.tag_id);
          collect(node.children);
        }
      }
    };
    collect(tagTree);

    setExpanded((prev) => {
      const next = { ...prev };
      for (const id of idsWithChildren) {
        if (next[id] === undefined) {
          next[id] = true;
        }
      }
      return next;
    });
  }, [tagTree]);

  const toggleNode = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAddChild = (node: TagTree) => {
    setAction("add");
    setTagId(undefined);
    const nextType = allowedChildrenByTagType[node.type][0];
    setPrefillParentTagId(node.tag_id);
    setPrefillType(nextType);
    setFormOpen(true);
  };

  const handleEdit = (node: TagTree) => {
    setAction("edit");
    setTagId(node.tag_id);
    setPrefillParentTagId(undefined);
    setPrefillType(undefined);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTag(deleteTarget.tag_id).unwrap();
      toast({
        variant: "success",
        title: "Success",
        description: "Business Unit deleted successfully",
      });
      setDeleteTarget(null);
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error?.data?.message || "Failed to delete business unit",
      });
    }
  };

  const renderNode = (node: TagTree, depth = 0): React.ReactNode => {
    const rowLeftRem = depth * 1.05 + 0.5;
    const lineLeftRem = rowLeftRem + 0.5;
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded[node.tag_id] ?? true;
    const isInactive = node.status === "inactive";
    const Icon = typeIconMap[node.type];

    return (
      <div key={node.tag_id} className="group/node relative">
        <div
          className={`group/item flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60 ${
            isInactive ? "text-muted-foreground/70" : ""
          }`}
          style={{ paddingLeft: `${rowLeftRem}rem` }}
        >
          {hasChildren ? (
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => toggleNode(node.tag_id)}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="inline-block h-4 w-4" />
          )}
          <Icon
            className={`h-4 w-4 ${isInactive ? "text-muted-foreground/60" : "text-muted-foreground"}`}
          />
          <button
            type="button"
            className={`text-sm ${isInactive ? "text-muted-foreground/80" : ""} hover:underline`}
            onClick={() => hasChildren && toggleNode(node.tag_id)}
          >
            {node.name}
          </button>
          <div className="ml-1 flex items-center gap-1 opacity-0 translate-y-1 transition-all duration-300 ease-out group-hover/item:opacity-100 group-hover/item:translate-y-0">
            <button
              type="button"
              title="Add child"
              className="flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-foreground hover:border-foreground/30 hover:-translate-y-0.5"
              style={{ transitionDelay: "30ms" }}
              onClick={() => handleAddChild(node)}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Edit business unit"
              className="flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-foreground hover:border-foreground/30 hover:-translate-y-0.5"
              style={{ transitionDelay: "80ms" }}
              onClick={() => handleEdit(node)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Delete business unit"
              disabled={isDeleting}
              className="flex h-6 w-6 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] hover:text-red-600 hover:border-red-300 hover:-translate-y-0.5 disabled:pointer-events-none disabled:opacity-60"
              style={{ transitionDelay: "130ms" }}
              onClick={() => setDeleteTarget(node)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="relative">
            <div
              className="pointer-events-none absolute bottom-2 top-0 border-l border-border opacity-0 transition-opacity duration-300 group-hover/node:opacity-100"
              style={{ left: `${lineLeftRem}rem` }}
            />
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search"}
          inputValue={search}
          setInputValue={setSearch}
        />
        {(search || filteredTree.length > 0) && (
          <div className="flex flex-wrap justify-start gap-2 md:gap-3 lg:flex-row lg:justify-end lg:items-end w-full">
            <Button 
              className="py-2 text-white flex gap-1 lg:mb-0 h-8 px-2 hover:bg-primary/90 hover:text-white"
              onClick={() => {
                setAction("add");
                setTagId(undefined);
                setPrefillParentTagId(undefined);
                setPrefillType(undefined);
                setFormOpen(true);
              }}
            >
              <i className="fa-solid fa-plus" />
              Create New
            </Button>
            {eventId && (
              <Button
                variant="outline"
                className="py-2 flex gap-1 lg:mb-0 h-8 px-2 w-32"
                onClick={() => setImportModalOpen(true)}
              >
                <i className="fa-solid fa-wrench" />
                Import
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="rounded-md border bg-background p-2">
        {isLoading ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">Loading tags...</div>
        ) : filteredTree.length > 0 ? (
          <div>{filteredTree.map((node) => renderNode(node))}</div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="bg-muted/30 p-6 rounded-full mb-4">
              <Folder className="h-10 w-10 text-muted-foreground/60" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No business units found</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-[300px]">
              {search 
                ? "No business units match your search criteria. Try a different search term or clear the filter."
                : "This event doesn't have any business units yet. Create a new one or import from global tags."
              }
            </p>
            {!search && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button 
                  className="text-white hover:bg-primary/90"
                  onClick={() => {
                    setAction("add");
                    setTagId(undefined);
                    setPrefillParentTagId(undefined);
                    setPrefillType(undefined);
                    setFormOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create New
                </Button>
                {eventId && (
                  <Button
                    className="w-32"
                    variant="outline"
                    onClick={() => setImportModalOpen(true)}
                  >
                    <i className="fa-solid fa-wrench mr-2" />
                    Import
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <TagsForm 
        open={formOpen}
        setOpen={setFormOpen}
        refetch={refetch}
        action={action}
        setAction={setAction}
        tagId={tagId}
        eventId={eventId}
        prefillParentTagId={prefillParentTagId}
        prefillType={prefillType}
        onClose={() => {
          setTagId(undefined);
          setPrefillParentTagId(undefined);
          setPrefillType(undefined);
        }}
      />
      <ConfirmationDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete business unit"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"? This action cannot be undone.`
            : "This action cannot be undone. Are you sure you want to proceed?"
        }
      />
      {eventId && (
        <TagImportModal
          open={importModalOpen}
          setOpen={setImportModalOpen}
          eventId={eventId}
          refetch={refetch}
        />
      )}
    </div>
  );
}

export default TagsTable;
